/* eslint-disable no-underscore-dangle */
import Cookies from 'js-cookie';
import isEqual from 'lodash/isEqual';

export enum IdentityEventTypes {
  UPDATED = 'UPDATED',
  UNAUTHORIZED = 'UNAUTHORIZED',
  ERROR = 'ERROR',
}

export type Identity = {
  initialJti: string;
  jti: string;
  grants?: {
    user?: {
      'ecom-eu': string[];
      'ecom-na': string[];
      golf: string[];
      lsid: string[];
      retail: string[];
    };
  };
  features?: string[];
};

export type IdentityResponse = {
  data?: Identity;
  ok: boolean;
  status: number;
};

type IdentityEvent =
  | {
      type: IdentityEventTypes.UPDATED;
      identity?: Identity;
    }
  | {
      type: IdentityEventTypes.UNAUTHORIZED;
    }
  | {
      type: IdentityEventTypes.ERROR;
      error: Error;
    };

type IdentityObserverSubscriber = (evt: IdentityEvent) => void;

type IdentityObserverOptions = {
  lsidIdentityServiceUrl: string;

  /**
   * Indicates whether the server endpoints use the auth-gateway
   * and thus whether we should rely on the JTI cookie for detecting session changes
   */
  usesAuthGateway?: boolean;
};

export class IdentityObserver {
  private _fetchInProgress?: Promise<IdentityResponse> | null;
  private _identityResponse?: IdentityResponse;
  private _jti?: string | null;
  private _options: IdentityObserverOptions;
  private _subscribers: Set<IdentityObserverSubscriber> = new Set();
  private _watcherUnsubscribeFunctions: (() => void)[] = [];

  constructor(options: IdentityObserverOptions) {
    this._options = options;

    if (!this._options.lsidIdentityServiceUrl) {
      throw new Error('[IdentityObserver] Missing option "lsidIdentityServiceUrl"');
    }
  }

  /**
   * Subscribe to changes to the current user's identity
   */
  subscribe(callback: IdentityObserverSubscriber): () => void {
    if (!this._watcherUnsubscribeFunctions.length) {
      this._initWatchers();
    }

    if (!this._subscribers.has(callback)) {
      this._subscribers.add(callback);
    }

    return () => {
      this._subscribers.delete(callback);

      // If this is the last subscription to be removed, clean up all the intervals and watcher functions
      if (this._subscribers.size === 0) {
        this.cleanupWatchers();
      }
    };
  }

  /**
   * Reach out to LSID service and return the current user's identity.
   *
   * If the response is not returned within 5 seconds, we reject with an error.
   *
   * This function is manually throttled so that we only have a single request pending at a time
   * So, if a request is already in flight, we will return the result of that instead of having multiple concurrent requests.
   *
   */
  async fetchIdentity(): Promise<IdentityResponse> {
    if (this._fetchInProgress) {
      return this._fetchInProgress;
    }

    this._fetchInProgress = Promise.race([
      await fetch(`${this._options.lsidIdentityServiceUrl}/auth/identity`, {
        credentials: 'include',
      }),
      // If our request takes more than 5 seconds, throw an error so we don't hang for a long time
      new Promise((_resolve, reject) =>
        setTimeout(() => reject(new Error('[IdentityObserver] Timed out fetching identity')), 5000)
      ) as Promise<never>,
    ])
      .then(async response => {
        const { ok, status } = response;

        // Keep the internal identityResponse value up-to-date any time we get a successful response
        let data;
        if (ok) {
          data = await response.json();
        }
        this._identityResponse = {
          data,
          ok,
          status,
        };
        this._fetchInProgress = null;

        return this._identityResponse;
      })
      .catch(error => {
        this._fetchInProgress = null;
        throw error;
      });

    return this._fetchInProgress;
  }

  /**
   * Remove all subscribers and clean up the watcher functions
   * This is mostly useful for cleanup during testing
   */
  cleanupWatchers() {
    this._watcherUnsubscribeFunctions.forEach(unsubscribe => {
      unsubscribe();
    });
    this._watcherUnsubscribeFunctions = [];
  }

  // Private

  private _publish(event: IdentityEvent) {
    this._subscribers.forEach(subscriber => {
      subscriber(event);
    });
  }

  private _initWatchers() {
    this._watcherUnsubscribeFunctions.push(
      this._watchIdentityOnTimer(),
      this._watchIdentityOnWindowFocus(),
      this._watchIdentityOnJTIChanges()
    );
  }

  /**
   * Reach out to the LSID service to determine whether our current session is valid
   * This call is throttled to avoid multiple calls within a short time period
   */
  private async _ensureValidSession() {
    try {
      const oldIdentityResponse = this._identityResponse;
      const response = await this.fetchIdentity();

      if (!response.ok) {
        if (response.status === 401) {
          this._publish({
            type: IdentityEventTypes.UNAUTHORIZED,
          });
        } else {
          throw new Error('[IdentityObserver] An error occurred trying to verify current session');
        }
      } else if (!isEqual(oldIdentityResponse, response)) {
        this._identityResponse = response;
        this._publish({
          type: IdentityEventTypes.UPDATED,
          identity: response.data,
        });
      }
    } catch (error) {
      this._publish({
        type: IdentityEventTypes.ERROR,
        error,
      });
    }
  }

  /**
   * New network requests may contain a new JTI cookie, which could indicate an updated or invalidated session
   * Check for a new value every 1 second
   * If the value has changed, reach out to LSID service to ensure our session is still valid
   */
  private _watchIdentityOnJTIChanges() {
    this._jti = Cookies.get('_ls_oidc_jti');

    const interval = setInterval(() => {
      if (!this._options.usesAuthGateway) {
        return;
      }

      if (this._fetchInProgress) {
        return;
      }

      const newJTI = Cookies.get('_ls_oidc_jti');
      const oldJTI = this._jti;
      this._jti = newJTI;

      if (newJTI !== oldJTI) {
        this._ensureValidSession();
      }
    }, 1000); // 1 second

    return () => {
      clearInterval(interval);
      this._jti = null;
    };
  }

  /**
   * Our LSID session may change without a corresponding network request to inform us
   * To detect these sort of changes, we check periodically whether our session is still valid
   *
   * This check is only used as a fallback when the auth-gateway is not being used
   */
  private _watchIdentityOnTimer() {
    const interval = setInterval(() => {
      if (this._options.usesAuthGateway) {
        return;
      }
      this._ensureValidSession();
    }, 30 * 1000); // 30 seconds

    return () => {
      clearInterval(interval);
    };
  }

  /**
   * Our LSID session may change after a period of inactivity
   * without a corresponding network request to inform us
   *
   * When refocusing the window or changing the tab visibility
   * we recheck our session to make sure it's still valid
   */
  private _watchIdentityOnWindowFocus() {
    const onFocus = () => {
      this._ensureValidSession();
    };
    window.addEventListener('focus', onFocus);

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        this._ensureValidSession();
      }
    };
    window.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }
}
